/*  src/App.jsx  —  versión completa (con Dashboard Admin nuevo)  */

import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import keycloak from "../keycloak";
import KeycloakContext from "../keycloakContext";
import ScrollToTop from "./components/ScrollToTop";
import PageTutorialOverlay from "./components/PageTutorialOverlay";
import ProfessorTutorialOverlay from "./components/ProfessorTutorialOverlay";

import {
  Navbar,
  StudentNavbar,
  Hero,
  About,
  Experiences,
  Tech,
  Works,
  Feedbacks,
  Contact,
  StarsCanvas,
} from "./components";
import Footer from "./components/Footer";
import "./i18n";
import "./index.css";
import { useConfiguratorStore } from "./store";
import { USERS_API } from "../api_calls";
import { getCurrentUserProfile } from "./services/userService";
import ProtectedRoute from "./components/ProtectedRoute";
import { ProfessorClassicBlockGate, ProfessorEditBlockRouteGate, ProfessorEditRouteGate } from "./components/ProfessorClassicSurveyGate";
import { getDefaultRouteForRole, getUserRoleFromGroups } from "./utils/accessPolicy";
import STEAMcatPage from "./pages/STEAMcatPage";
import Dossier from "./pages/DossierPage.jsx";

/* ✅ NUEVO: Dashboard Admin (MVP) */
import DashboardPage from "./pages/DashboardPage.jsx";

/* ─────────────── páginas lazy ─────────────── */
const ProjectPage = lazy(() => import("./pages/ProjectPage"));
const InstructionsPage = lazy(() => import("./pages/InstructionsPage"));
const IniciPage = lazy(() => import("./pages/IniciPage"));
const Edit = lazy(() => import("./pages/Edit"));
const SurveyReadOnlyPage = lazy(() => import("./pages/SurveyReadOnlyPage"));
const EditBlockWrapper = lazy(() => import("./pages/EditBlockWrapper"));
const JourneyPage = lazy(() => import("./pages/JourneyPage"));
const AdvancedToolsPage = lazy(() => import("./pages/AdvancedToolsPage"));
const ClassroomPage = lazy(() => import("./pages/ClassroomPage.jsx"));
const StudentAccountPage = lazy(() => import("./pages/StudentAccountPage"));
const StudentDashboardPage = lazy(() => import("./pages/StudentDashboardPage"));
const MapPage = lazy(() => import("./pages/MapPage"));
const FamiliesPage = lazy(() => import("./pages/FamiliesPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const MembersPage = lazy(() => import("./pages/MembersPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const CompoPrendreMostres = lazy(() =>
  import("./components/CompoPrendreMostres")
);
const SituacioApren = lazy(() => import("./pages/SituacioApren"));
const Material = lazy(() => import("./pages/MaterialPage"));
const StudentInfoPage = lazy(() => import("./pages/StudentInfoPage"));
const StudentAccountDashboard = lazy(() =>
  import("./pages/StudentAccountDashboard")
);
const TechnicalSupportPage = lazy(() =>
  import("./pages/TechnicalSupport.jsx")
);
const RegisterTeacherPage = lazy(() => import("./pages/RegisterTeacherPage"));
const TeacherRegistrationSubmittedPage = lazy(() => import("./pages/TeacherRegistrationSubmittedPage"));
const TeacherRegistrationStatusPage = lazy(() => import("./pages/TeacherRegistrationStatusPage"));
const TeacherApprovalPage = lazy(() => import("./pages/TeacherApprovalPage"));
const TeacherRegistrationActivatedPage = lazy(() => import("./pages/TeacherRegistrationActivatedPage"));
const ProfessorOnboardingPage = lazy(() => import("./pages/ProfessorOnboardingPage"));

/* ─────────────── blocs de creación ─────────────── */
const Bloc0Page = lazy(() => import("./components/blocs/Bloc0page"));
const Bloc1Page = lazy(() => import("./components/blocs/Bloc1page"));
const Bloc2Page = lazy(() => import("./components/blocs/Bloc2page"));
const Bloc3Page = lazy(() => import("./components/blocs/Bloc3page"));
const Bloc4Page = lazy(() => import("./components/blocs/Bloc4page"));
const Bloc5Page = lazy(() => import("./components/blocs/Bloc5page"));
const Bloc6Page = lazy(() => import("./components/blocs/Bloc6page"));
const Bloc7Page = lazy(() => import("./components/blocs/Bloc7page"));
const GamePage = lazy(() => import("./pages/GamePage"));

/* ─────────────── otros componentes ─────────────── */

const App = () => {
  /* ─── acceso al store ─── */
  const setAccountLevel = useConfiguratorStore((s) => s.setAccountLevel);
  const setUnlockedTrophies = useConfiguratorStore((s) => s.setUnlockedTrophies);
  const clearUnlockedTrophies = useConfiguratorStore((s) => s.clearUnlockedTrophies);
  const setUnlockedAchievements = useConfiguratorStore(
    (s) => s.setUnlockedAchievements
  );
  const clearUnlockedAchievements = useConfiguratorStore(
    (s) => s.clearUnlockedAchievements
  );
  const setUnlockedAssets = useConfiguratorStore((s) => s.setUnlockedAssets);
  const clearUnlockedAssets = useConfiguratorStore((s) => s.clearUnlockedAssets);

  /* ─── carga inicial del usuario ─── */
  useEffect(() => {
    if (keycloak.authenticated) {
      getCurrentUserProfile(keycloak)
        .then((data) => {
          setAccountLevel(data.level || 1);
          setUnlockedTrophies(data.trophies || []);
          setUnlockedAchievements(data.achievements || []);
          setUnlockedAssets(data.assets || []);
        })
        .catch(() => {
          setAccountLevel(1);
          clearUnlockedTrophies();
          clearUnlockedAchievements();
          clearUnlockedAssets();
        });
    } else {
      setAccountLevel(1);
      clearUnlockedTrophies();
      clearUnlockedAchievements();
      clearUnlockedAssets();
    }
  }, [
    keycloak.authenticated,
    setAccountLevel,
    setUnlockedTrophies,
    clearUnlockedTrophies,
    setUnlockedAchievements,
    clearUnlockedAchievements,
    setUnlockedAssets,
    clearUnlockedAssets,
  ]);

  /* ─── determinar rol ─── */
  const groups = keycloak.tokenParsed?.groups || [];
  const userRole = getUserRoleFromGroups(groups);
  const defaultRoute = getDefaultRouteForRole(userRole);

  return (
    <KeycloakContext.Provider value={keycloak}>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div>Loading...</div>}>
          <div className="flex min-h-screen w-full min-w-0">
            {/* Solo alumno autenticado usa la StudentNavbar */}
            {keycloak.authenticated && userRole === "alumne" ? (
              <StudentNavbar />
            ) : (
              <Navbar />
            )}

            {/* Aplicar ml-64 únicamente si alumno autenticado */}
            <div
              className={`flex-1 w-full min-w-0 ${
                keycloak.authenticated && userRole === "alumne" ? "md:ml-64" : ""
              }`}
            >
              <PageTutorialOverlay />
              <ProfessorTutorialOverlay />

              <Routes>
                {/* ────────── home / default entry ────────── */}
                <Route
                  path="/"
                  element={
                    keycloak.authenticated && userRole === "alumne" ? (
                      <Navigate to={defaultRoute} replace />
                    ) : (
                      <>
                        <div className="relative bg-hero-pattern bg-cover bg-no-repeat bg-center opacity-90">
                          <div className="blob"></div>
                          <Hero />
                        </div>
                        <div className="page-divider" style={{ paddingTop: "10rem" }}>
                          <Works />
                        </div>
                        <div className="gradient-background">
                          <Experiences />
                          <Tech />
                          <About />
                          <Feedbacks />
                        </div>
                        <div className="relative bg-contact-pattern bg-cover bg-no-repeat bg-center">
                          <Contact />
                          <StarsCanvas />
                        </div>
                      </>
                    )
                  }
                />

                {/* ────────── rutas públicas ────────── */}
                <Route path="/projecte" element={<ProjectPage />} />
                <Route path="/instructions" element={<InstructionsPage />} />
                <Route path="/map" element={<MapPage />} />
                <Route
                  path="/game"
                  element={
                    <ProtectedRoute access="journeyEntry">
                      <GamePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/technical-support" element={<TechnicalSupportPage />} />
                <Route path="/registre-professorat" element={<RegisterTeacherPage />} />
                <Route path="/teacher-registration/submitted" element={<TeacherRegistrationSubmittedPage />} />
                <Route path="/teacher-registration/status" element={<TeacherRegistrationStatusPage />} />
                <Route path="/teacher-registration/activated" element={<TeacherRegistrationActivatedPage />} />
                <Route path="/teacher-approval" element={<TeacherApprovalPage />} />

                <Route
                  path="/professor/onboarding"
                  element={
                    <ProtectedRoute roles={["professor"]}>
                      <ProfessorOnboardingPage />
                    </ProtectedRoute>
                  }
                />

                {/* ────────── Dashboard alumno (se mantiene) ────────── */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute roles={["alumne"]} access="studentDashboard">
                      <StudentDashboardPage />
                    </ProtectedRoute>
                  }
                />

                {/* ────────── ✅ Dashboard Admin nuevo (prof/admin) ────────── */}
                <Route
                  path="/dashboard-admin"
                  element={
                    <ProtectedRoute roles={["professor", "admin"]}>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/steamcat" element={<STEAMcatPage />} />

                {/* ────────── rutas protegidas (genéricas) ────────── */}
                <Route
                  path="/applicatiu"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <IniciPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/edit/:id"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorEditRouteGate>
                        <Edit />
                      </ProfessorEditRouteGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/edit/:surveyId/bloque/:blockId"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorEditBlockRouteGate>
                        <EditBlockWrapper />
                      </ProfessorEditBlockRouteGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/survey/:id/view"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <SurveyReadOnlyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/journey/:surveyId"
                  element={
                    <ProtectedRoute access="journey">
                      <JourneyPage />
                    </ProtectedRoute>
                  }
                />

                {/* ────────── creación de blocs ────────── */}
                <Route
                  path="/bloque/0"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorClassicBlockGate>
                        <Bloc0Page />
                      </ProfessorClassicBlockGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bloque/1"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorClassicBlockGate>
                        <Bloc1Page />
                      </ProfessorClassicBlockGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bloque/2"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorClassicBlockGate>
                        <Bloc2Page />
                      </ProfessorClassicBlockGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bloque/3"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorClassicBlockGate>
                        <Bloc3Page />
                      </ProfessorClassicBlockGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bloque/4"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorClassicBlockGate>
                        <Bloc4Page />
                      </ProfessorClassicBlockGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bloque/5"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorClassicBlockGate>
                        <Bloc5Page />
                      </ProfessorClassicBlockGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bloque/6"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorClassicBlockGate>
                        <Bloc6Page />
                      </ProfessorClassicBlockGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bloque/7"
                  element={
                    <ProtectedRoute access="classicSurvey">
                      <ProfessorClassicBlockGate>
                        <Bloc7Page />
                      </ProfessorClassicBlockGate>
                    </ProtectedRoute>
                  }
                />

                {/* ────────── profesor / admin ────────── */}
                <Route
                  path="/advanced-tools"
                  element={
                    <ProtectedRoute roles={["professor"]}>
                      <AdvancedToolsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/situacio"
                  element={
                    <ProtectedRoute roles={["professor", "admin"]}>
                      <SituacioApren />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/materials"
                  element={
                    <ProtectedRoute roles={["professor", "admin"]}>
                      <Material />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dossier/*"
                  element={
                    <ProtectedRoute roles={["professor", "admin"]}>
                      <Dossier />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/aula"
                  element={
                    <ProtectedRoute roles={["professor", "admin"]}>
                      <ClassroomPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/:studentId"
                  element={
                    <ProtectedRoute roles={["professor", "admin"]}>
                      <StudentAccountPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/families"
                  element={
                    <ProtectedRoute roles={["professor", "admin"]}>
                      <FamiliesPage />
                    </ProtectedRoute>
                  }
                />

                {/* ────────── cuenta compartida ────────── */}
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      {userRole === "alumne" ? (
                        <StudentAccountDashboard />
                      ) : (
                        <AccountPage />
                      )}
                    </ProtectedRoute>
                  }
                />

                <Route path="/info" element={<StudentInfoPage />} />

                <Route
                  path="/members"
                  element={
                    <ProtectedRoute>
                      <MembersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <ProtectedRoute>
                      <ResetPasswordPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/prendre-mostres"
                  element={
                    <ProtectedRoute>
                      <CompoPrendreMostres />
                    </ProtectedRoute>
                  }
                />
              </Routes>

              <Footer />
            </div>
          </div>
        </Suspense>
      </BrowserRouter>
    </KeycloakContext.Provider>
  );
};

export default App;
